import COUPON_MANAGER from '@/lib/basket/COUPON_MANAGER'
import POT_MANAGER from '@/lib/basket/POT_MANAGER'
import TRANSITAIRE_MANAGER from '@/lib/basket/TRANSITAIRE_MANAGER'
import SHIPPING_MANAGER from '@/lib/basket/SHIPPING_MANAGER'
import { useEffect, useMemo, useState } from 'react'
import { useRestQuery } from '@/lib/hook/tanstackQuery'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'
import { parseBasket } from '@/lib/basket/parseBasket'
import Basket from '@/type/basket'

/**
 * Hook basket: TanStack Query + REST endpoint cart_content (CS-Cart entity).
 *
 * Backend: PharmeCartContent.php (app/addons/ph_front_rest_api/Tygh/Api/Entities/v50)
 * appelle CartContentService::getCartContent avec params query string.
 */
const getShippingDefault = (basket: any) => {
  if (!basket || !basket.product_groups) return []
  const stored = SHIPPING_MANAGER.list()
  return basket.product_groups.map((pg, index) => {
    const shipping = stored?.find(s => s.group_key == pg.group_key)
    return {
      shipping_group_key: pg.shippings?.[0]?.group_key || index,
      group_key: pg.group_key,
      company_id: pg.company_id,
      shipping_id: shipping?.shipping_id || pg.shippings?.[0]?.shipping_id || 0,
    }
  })
}

/**
 * Selection des modes de livraison a envoyer au serveur, sous la forme `group_key -> shipping_id`.
 *
 * Une entree sans `group_key` ou sans `shipping_id` exploitable est ecartee : le serveur
 * appliquera le mode par defaut du groupe concerne. On ne renvoie JAMAIS une liste tronquee
 * dont le rattachement au groupe reposerait sur l'ordre.
 */
const buildShippingSelection = (stored: any[] | undefined | null): Record<string, number> =>
  (stored ?? []).reduce((selection, entry) => {
    const groupKey = entry?.group_key
    const shippingId = Number(entry?.shipping_id)

    if (groupKey === undefined || groupKey === null || groupKey === '' || !shippingId) {
      return selection
    }

    return { ...selection, [String(groupKey)]: shippingId }
  }, {} as Record<string, number>)

const useBasket = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const [shippingIds, setShippingIds] = useState(null)

  // Variables source = managers (localStorage). Lecture une seule fois au mount pour stabiliser queryKey.
  const [queryParamsKey] = useState(() =>
    JSON.stringify({
      // R1 — le mode de livraison est envoye INDEXE PAR GROUPE. Le serveur attend cette forme
      // (il indexe `chosen_shipping` par group_key et applique le mode par defaut du groupe
      // quand la cle manque). L'ancienne liste plate ne se rattachait aux groupes que par
      // l'ordre : un id pouvait etre applique au mauvais vendeur, et un `filter(Boolean)` sur
      // un groupe sans mode de livraison decalait tout le reste, ce qui faisait sauter le
      // calcul entier et ressortir le panier a 0 EUR de port.
      shipping_ids: buildShippingSelection(SHIPPING_MANAGER.list()),
      coupon_codes: COUPON_MANAGER.list() ?? [],
      pot_used: POT_MANAGER.get() ?? false,
      chosen_transitaire_id: TRANSITAIRE_MANAGER.get() ?? null,
    })
  )

  const queriesParams = useMemo(() => {
    const parsed = JSON.parse(queryParamsKey)
    return {
      shipping_ids: parsed.shipping_ids,
      coupon_codes: parsed.coupon_codes,
      has_pot: false,
      pot_used: parsed.pot_used,
      ...(parsed.chosen_transitaire_id
        ? { chosen_transitaire_id: parsed.chosen_transitaire_id }
        : {}),
    }
  }, [queryParamsKey])

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useRestQuery<{ status: number; data: Basket }>({
    endpoint: ApiQueryEndpoint.BASKET.GET_BASKET.endpoint,
    queriesParams,
    enabled: !!isAuthenticated,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 30,
  })

  // CartContentService retourne { status, data: <basket raw> }. Parse front-side.
  const data = useMemo(() => parseBasket(response ?? null), [response])

  // Sync shippingIds + localStorage quand basket data change (skip pendant fetch en cours)
  useEffect(() => {
    if (isFetching || !data) return
    const next = getShippingDefault(data)
    const current = SHIPPING_MANAGER.list() ?? []
    if (JSON.stringify(current) === JSON.stringify(next)) return
    setShippingIds(next)
    SHIPPING_MANAGER._set(next)
  }, [isFetching, data?.product_groups?.length ?? 0])

  return useMemo(
    () => ({
      data,
      loading: !data,
      // isFetching (et pas isLoading) : couvre aussi les refetchs declenches par
      // basketAllRefetcher (ajout produit, import CSV...) pour afficher le
      // bandeau "panier en cours de mise a jour" sans masquer le contenu.
      loadingRefetchBasket: isFetching,
      refetch,
      shippingIds,
      setShippingIds,
    }),
    [data, isFetching, refetch, shippingIds]
  )
}

export default useBasket
